import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router';
import {
    SAVED_SEARCHES_QUERY,
    ADD_SAVED_SEARCH,
    UPDATE_SAVED_SEARCH,
} from '@general/graphql/saved-searches.graph';

const emptyFormObject = {
    title: null,
    primaryInterest: null,
    businessLocations: [],
    tenants: {
        purpose: [],
        density: [],
        size: [],
        typeOfCenter: [],
        typeOfFacility: [],
    },
    space: {
        purpose: [],
        density: [],
        size: [],
        typeOfCenter: [],
        typeOfFacility: [],
    },
    connections: {
        connectionType: null,
    },
    products: {
        areYou: null,
        productTypes: null,
    },
};

const ProgressiveProfilingForm = ({
    id,
    addNew = false,
    saveAsDefault = false,
    unlock,
    steps,
    success,
    onLoad = () => {},
    onNext = () => {},
    onComplete,
}) => {
    const [profile, setProfile] = useState(null);
    const [formData, setFormData] = useState({});
    const [step, setStep] = useState(unlock ? 0 : 1);

    const { loading, error, data } = useQuery(SAVED_SEARCHES_QUERY);
    const [updateSearch] = useMutation(UPDATE_SAVED_SEARCH);
    const [addSearch] = useMutation(ADD_SAVED_SEARCH);

    if (error) throw error;

    useEffect(() => {
        if (!loading && !profile && data.savedSearches) {
            let search = emptyFormObject;

            if (!addNew && data.savedSearches.length) {
                const foundSearch = id
                    ? data.savedSearches.find(search => search.id === id)
                    : data.savedSearches.find(search => search.default === true);

                search = foundSearch || search;
            }

            setProfile(search);
            setFormData(search);
            onLoad(search);
        }
    }, [addNew, data, id, loading, onLoad, profile]);

    const next = (input = {}) => {
        const newData = { ...formData, ...input };

        setFormData(newData);
        onNext();

        if (step >= steps.length) {
            submit(newData).then(() => {
                setStep(step + 1);
            });
        } else {
            setStep(step + 1);
        }
    };

    const back = (input = {}) => {
        if (step >= 1) {
            setStep(step - 1);
        }
    };

    const submit = async input => {
        let variables = {};

        const connections = { connectionType: input.connections.connectionType };

        const space = {
            density: input.space.density,
            purpose: input.space.purpose,
            size: input.space.size,
            typeOfCenter: input.space.typeOfCenter,
            typeOfFacility: input.space.typeOfFacility,
        };

        const tenants = {
            density: input.tenants.density,
            purpose: input.tenants.purpose,
            size: input.tenants.size,
            typeOfCenter: input.tenants.typeOfCenter,
            typeOfFacility: input.tenants.typeOfFacility,
        };

        const products = {
            areYou: input.products.areYou,
            productTypes: input.products.productTypes,
        };

        variables = { ...input, connections, products, tenants, space, default: saveAsDefault };

        if (saveAsDefault) {
            variables.title = `Default - ${input.primaryInterest}`;
        }

        if (input.id) {
            return updateSearch({ variables });
        }

        return addSearch({
            variables,
            update: (cache, { data: savedSearchData }) => {
                const { savedSearches: existingItems } = cache.readQuery({ query: SAVED_SEARCHES_QUERY });
                const newItem = savedSearchData.addSavedSearch;

                cache.writeQuery({
                    query: SAVED_SEARCHES_QUERY,
                    data: {
                        savedSearches: [...existingItems, newItem],
                    },
                });
            },
        });
    };

    const getCurrentStepComponent = currentStepNumber => {
        if (currentStepNumber === 0) {
            return unlock;
        }

        if (currentStepNumber > 0 && currentStepNumber <= steps.length) {
            return steps[step - 1];
        }

        if (currentStepNumber >= steps.length && success) {
            return success;
        }

        return null;
    };

    const CurrentStep = getCurrentStepComponent(step);

    return CurrentStep ? (
        <CurrentStep onNext={next} onBack={back} loading={loading} data={formData} complete={onComplete} />
    ) : (
        <Redirect to="/my-icsc/connections/matches" />
    );
};

ProgressiveProfilingForm.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    addNew: PropTypes.bool,
    saveAsDefault: PropTypes.bool,
    unlock: PropTypes.elementType,
    steps: PropTypes.array,
    success: PropTypes.elementType,
    onLoad: PropTypes.func,
    onNext: PropTypes.func,
    onComplete: PropTypes.func,
};

export default ProgressiveProfilingForm;
